import {
  Component, ElementRef, OnInit, DoCheck, Input, Output, EventEmitter, ContentChild, ContentChildren, QueryList
} from '@angular/core';
import { CfCoreComponent } from '../core/core.component';
import { IconModel } from '../../models/icon/icon.model';
import { ButtonModel } from '../../models/button/button.model';
import { WizardModel } from '../../models/wizard/wizard.model';
import { WizardStylingModel } from '../../models/wizard/wizard-styling.model';
import { CfWizardStepComponent } from '../wizard-step/wizard-step.component';
import { CfWizardFooterComponent } from '../wizard-footer/wizard-footer.component';
import { CfWizardBackComponent } from '../wizard-back/wizard-back.component';
import { CfWizardNextComponent } from '../wizard-next/wizard-next.component';
import { CfWizardFinishComponent } from '../wizard-finish/wizard-finish.component';
import { WizardTemplates } from '../../templates/wizard.template';
import { TemplateService } from '../../services/template-service/template.service';
/**
 * <p>Wizard component based on Angular Material Tabs and Angular animations system</p>
 * <p>Example of using:</p>
 * <pre>
 * <code><</code>cf-wizard [properties]="myWizard" [styling]="myWizardStyles"<code>></code>
 * <code><</code><code>/</code>cf-wizard<code>></code>
 * </pre>
 */
export class CfWizardComponent extends CfCoreComponent implements OnInit, DoCheck {
/**
 * It is an attribute <b>[properties]</b> of directive <b>cf-wizard</b> which take WizardModel object.
 * <p>Example of model:</p>
 * <code><pre>{{'{'}}
 *  activeStepIndex: 3,
 *  showStepNumberAsIcon: true,
 *  showStepNumberAsPrefix: false,
 *  indexBefore: true,
 *  backButton: this.myPrevBtn,
 *  nextButton: this.myNextBtn,
 *  finishButton: this.myDoneBtn,
 *  headerPosition: 'above',
 *  footerPosition: 'below'
 * {{'}'}}</pre></code>
 * <h4>activeStepIndex: number</h4>
 * Active step index
 * <h4>showStepNumberAsIcon: boolean</h4>
 * Showing step number as icon in wizard header steps buttons
 * <h4>showStepNumberAsPrefix: boolean</h4>
 * Showing step prefix as icon in wizard header steps buttons
 * <h4>indexBefore: boolean</h4>
 * Showing header step index (prefix or icon) before step label
 * <h4>backButton: ButtonModel</h4>
 * ButtonModel object for wizard previous button
 * <h4>nextButton: ButtonModel</h4>
 * ButtonModel object for wizard next button
 * <h4>finishButton: ButtonModel</h4>
 * ButtonModel object for wizard finish button
 * <h4>headerPosition: string</h4>
 * Header position: above (default), right, below, left
 * <h4>footerPosition: string</h4>
 * Footer position: above, right, below (default), left
 */
properties: WizardModel;
/**
 * It is an attribute <b>[styling]</b> of directive <b>cf-wizard</b> which take WizardStylingModel object with next properties:
 * <h4>headerSection: StylingModel</h4>
 * Styling properties for wizard header container
 * <h4>container: StylingModel</h4>
 * Styling properties for main wizard container
 * <h4>navigationSection: StylingModel</h4>
 * Styling properties for wizard bottom navigation section
 * <h4>backButton: ButtonStylingModel</h4>
 * Styling properties for wizard previous button
 * <h4>nextButton: ButtonStylingModel</h4>
 * Styling properties for wizard next button
 * <h4>finishButton: ButtonStylingModel</h4>
 * Styling properties for wizard finish button
 */
styling: WizardStylingModel;
/**
 * Active step index
 * @param {?} value
 * @return {?}
 */
set activeStepIndex(value: number) { this.properties.activeStepIndex = value; }
/**
 * If to show step number as icon in wizard header steps buttons.
 * @param {?} value
 * @return {?}
 */
set showStepNumberAsIcon(value: boolean) { this.properties.showStepNumberAsIcon = value; }
/**
 * If to show step number as prefix in wizard header steps buttons.
 * @param {?} value
 * @return {?}
 */
set showStepNumberAsPrefix(value: boolean) { this.properties.showStepNumberAsPrefix = value; }
/**
 * If to show step number before the label.
 * @param {?} value
 * @return {?}
 */
set indexBefore(value: boolean) { this.properties.indexBefore = value; }
/**
 * Header position: above (default), right, below, left.
 * @param {?} value
 * @return {?}
 */
set headerPosition(value: string) { this.properties.headerPosition = value; }
/**
 * Footer position: above, right, below (default), left.
 * @param {?} value
 * @return {?}
 */
set footerPosition(value: string) { this.properties.footerPosition = value; }
/**
 * The ButtonModel object for wizard previous button.
 * @param {?} value
 * @return {?}
 */
set backButton(value: ButtonModel) { this.properties.backButton = value; }
/**
 * The ButtonModel object for wizard next button.
 * @param {?} value
 * @return {?}
 */
set nextButton(value: ButtonModel) { this.properties.nextButton = value; }
/**
 * The ButtonModel object for wizard finish button.
 * @param {?} value
 * @return {?}
 */
set finishButton(value: ButtonModel) { this.properties.finishButton = value; }
/**
 * @hidden 
 * It is an array of wizard steps
 */
wizardSteps: QueryList<CfWizardStepComponent>;
/**
 * @hidden
 */
wizardFooter: CfWizardFooterComponent;
/**
 * @hidden
 */
wizardBack: CfWizardBackComponent;
/**
 * @hidden
 */
wizardNext: CfWizardNextComponent;
/**
 * @hidden
 */
wizardFinish: CfWizardFinishComponent;
/**
 * @hidden
 */
private _activeStep: CfWizardStepComponent;
/**
 * @hidden 
 * Property which means if wizard Finish step was clicked
 */
private _isCompleted = false;
/**
 * Output event with active step index
 */
onStepChanged: EventEmitter<CfWizardStepComponent> = new EventEmitter<CfWizardStepComponent>();
/**
 * Output event on wizard work finish
 */
onComplete: EventEmitter<any> = new EventEmitter<any>();
/**
 * @hidden
 * @param {?} elementRef
 * @param {?} templateService
 */
constructor(public elementRef: ElementRef, /**@hidden */ templateService: TemplateService) {
    super(elementRef, templateService);
    this.properties = new WizardModel();
    this.styling = new WizardStylingModel();
  }
/**
 * @hidden
 * @return {?}
 */
ngOnInit() {
    this.getMyTemplate('wizard', WizardTemplates).then(() => {
      this.mergeProperties(this.activeTemplate.property, this.properties, 'WIZARD').then( sampler => {
        this._initializeWizard(this.properties.activeStepIndex);
        // console.log('WIZARD', this);
      });
    });
  }
/**
 * @hidden
 * @return {?}
 */
ngAfterContentInit() {
    if (this.wizardBack) { this.wizardBack.onClick.subscribe(e => this.back()); }
    if (this.wizardNext) { this.wizardNext.onClick.subscribe(e => this.next()); }
    if (this.wizardFinish) { this.wizardFinish.onClick.subscribe(e => this.complete()); }
  }
/**
 * @hidden 
 * Checking changes in component properties
 * @return {?}
 */
ngDoCheck() {
    if (this.wizardSteps) { this._findInvalidStep(); this._setNavButtonsVisibility(); }
  }
/**
 * @hidden 
 * Method to set initial state of wizard
 * @param {?} index  active step index
 * @return {?}
 */
_initializeWizard(index) {
    let /** @type {?} */ initIndex = 0;
    let /** @type {?} */ stepsLength = this.wizardSteps.toArray().length;
    if (typeof index === 'number' && index > -1 && index < stepsLength && !!this.wizardSteps.toArray()[index]) {
      initIndex = index;
    }
    this._activeStep = this.wizardSteps.toArray()[initIndex];
  }
/**
 * @hidden 
 * Method to set active step index and emit event with it
 * @param {?} index  active step index
 * @return {?}
 */
_stepChanged(index) {
    if (index !== this.properties.activeStepIndex) {
      this.properties.activeStepIndex = index;
    }
    this._activeStep = this.wizardSteps.toArray()[index];
    this.onStepChanged.emit(index);
  }
/**
 * @hidden 
 * Method to check if it is possible to go to step which is not disabled
 * @param {?} index  step index
 * @param {?} direction  direction
 * @return {?}
 */
_checkPossibleStep(index, direction) {
    const /** @type {?} */ tempIndex = index;
    if (!this._isCompleted) {
      if (direction === 'back' && index > 0) {
        if (this.wizardSteps.toArray()[tempIndex - 1].properties.disable === false) {
          this.properties.activeStepIndex = tempIndex - 1;
        } else {
          this._checkPossibleStep(tempIndex - 1, 'back');
        }
      }
      if (direction === 'next' && index < this.wizardSteps.toArray().length) {
        if (this.wizardSteps.toArray()[tempIndex + 1].properties.disable === false) {
          this.properties.activeStepIndex = tempIndex + 1;
        } else {
          this._checkPossibleStep(tempIndex + 1, 'next');
        }
      }
    }
  }
/**
 * @hidden 
 * Method to go to previous step
 * @return {?}
 */
back() {
    const /** @type {?} */ tempIndex = this.properties.activeStepIndex;
    this._checkPossibleStep(tempIndex, 'back');
  }
/**
 * @hidden 
 * Method to go to next step
 * @return {?}
 */
next() {
    const /** @type {?} */ tempIndex = this.properties.activeStepIndex;
    this._checkPossibleStep(tempIndex, 'next');
  }
/**
 * @hidden 
 * Function to set wizard to completed state and emit event for that
 * @return {?}
 */
complete() {
    this.onComplete.emit();
    this._isCompleted = true;
  }
/**
 * @hidden 
 * Function for checking invalid steps and manipulate with wizard button disable states
 * @return {?}
 */
private _findInvalidStep() {
    let /** @type {?} */ invalidStepIndex = -1, /** @type {?} */ hasInvalidStep = false;
    for (let /** @type {?} */ i = 0; i < this.wizardSteps.length; ++i) {
      if (this.wizardSteps.toArray()[i].properties && this.wizardSteps.toArray()[i].properties.isValid === false) {
        invalidStepIndex = i;
        hasInvalidStep = true;
        break;
      }
    }
    if (hasInvalidStep) {
      for (let /** @type {?} */ i = invalidStepIndex + 1; i < this.wizardSteps.length; ++i) {
        this.wizardSteps.toArray()[i].properties.isValid = false;
      }
    }
  }
/**
 * @hidden
 * @return {?}
 */
private _setNavButtonsVisibility() {
    if (this.properties && typeof this.properties.activeStepIndex === 'number') {
      const /** @type {?} */ i = this.properties.activeStepIndex, /** @type {?} */ quantity = this.wizardSteps.length;
      if (this.wizardBack) {
        this.wizardBack.nativeElement.hidden = i === 0 ? true : false;
      }
      if (this.wizardNext) {
        this.wizardNext.nativeElement.hidden = i === quantity - 1 ? true : false;
      }
      if (this.wizardFinish) {
        this.wizardFinish.nativeElement.hidden = i !== quantity - 1 ? true : false;
      }
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'cf-wizard',
  template: "<div *ngIf=\"_inited && properties?.display\" class=\"header-{{properties?.headerPosition}} footer-{{properties?.footerPosition}} {{activeTemplate.style?.container?.class}} {{styling?.container?.class}} {{getDynamicClasses(activeTemplate.style?.container?.dynamicClass)}} {{getDynamicClasses(styling?.container?.dynamicClass)}} {{_isCompleted ? 'completed' : ''}}\" [ngClass]=\"'cf-wizard-container'\"> <div class=\"cf-wizard-content\"> <md-tab-group class=\"mat-tab-group\" [headerPosition]=\"properties.headerPosition\" [selectedIndex]=\"properties.activeStepIndex\" (selectedIndexChange)=\"_stepChanged($event)\"> <md-tab *ngFor=\"let step of wizardSteps; let i = index\" [disabled]=\"!step?.properties?.isValid || step?.properties?.disable || _isCompleted\" class=\"cf-wizard-step\"> <ng-template md-tab-label> <md-ink-bar class=\"mat-ink-bar step-ink-bar\"></md-ink-bar> <div class=\"{{activeTemplate.style?.headerSection?.class}} {{styling?.headerSection?.class}} {{getDynamicClasses(activeTemplate.style?.headerSection?.dynamicClass)}} {{getDynamicClasses(styling?.headerSection?.dynamicClass)}}\" [ngClass]=\"'cf-step-header'\"> <span *ngIf=\"properties?.showStepNumberAsIcon\" class=\"{{step?.activeTemplate?.style?.iconIndex?.class}} {{step?.styling?.iconIndex?.class}} {{getDynamicClasses(step?.activeTemplate?.style?.iconIndex?.dynamicClass)}} {{getDynamicClasses(step?.styling?.iconIndex?.dynamicClass)}}\" [ngClass]=\"'cf-step-index-icon'\">{{i + 1}}</span> <span *ngIf=\"properties?.showStepNumberAsPrefix\" class=\"{{step?.activeTemplate?.style?.prefixIndex?.class}} {{step?.styling?.prefixIndex?.class}} {{getDynamicClasses(step?.activeTemplate?.style?.prefixIndex?.dynamicClass)}} {{getDynamicClasses(step?.styling?.prefixIndex?.dynamicClass)}}\" [ngClass]=\"'cf-step-index-prefix'\">{{i + 1}}</span> <div class=\"{{step?.activeTemplate?.style?.label?.class}} {{step?.styling?.label?.class}} {{getDynamicClasses(step?.activeTemplate?.style?.label?.dynamicClass)}} {{getDynamicClasses(step?.styling?.label?.dynamicClass)}}\" [ngClass]=\"'cf-step-label'\"> <label>{{step?.properties?.label}}</label> <span><span [ngTemplateOutlet]=\"step.stepHeader\"></span></span> </div> </div> </ng-template> <div class=\"{{step?.activeTemplate?.style?.container?.class}} {{step?.styling?.container?.class}} {{getDynamicClasses(step?.activeTemplate?.style?.container?.dynamicClass)}} {{getDynamicClasses(step?.styling?.container?.dynamicClass)}}\" [ngClass]=\"'cf-step-container'\"> <span [ngTemplateOutlet]=\"step.stepContent\"></span> </div>     </md-tab> </md-tab-group> </div> <div *ngIf=\"!_isCompleted\" class=\"{{activeTemplate.style?.navigationSection?.class}} {{styling?.navigationSection?.class}} {{getDynamicClasses(activeTemplate.style?.navigationSection?.dynamicClass)}} {{getDynamicClasses(styling?.navigationSection?.dynamicClass)}}\" [ngClass]=\"'cf-wizard-footer'\"> <div *ngIf=\"!wizardFooter\"> <cf-button [hidden]=\"!(properties.activeStepIndex > 0)\" [properties]=\"properties?.backButton\" [styling]=\"styling?.backButton\" (onClick)=\"back()\"></cf-button> <cf-button [hidden]=\"!(properties.activeStepIndex < wizardSteps?.length - 1)\" [disable]=\"!_activeStep.properties.isValid\" [properties]=\"properties?.nextButton\" [styling]=\"styling?.nextButton\" (onClick)=\"next()\"></cf-button> <cf-button [hidden]=\"!(properties.activeStepIndex === wizardSteps?.length - 1)\" [disable]=\"!_activeStep.properties.isValid\" [properties]=\"properties?.finishButton\" [styling]=\"styling?.finishButton\" (onClick)=\"complete()\"></cf-button> </div> <div *ngIf=\"wizardFooter\"> <ng-content select=\"cf-wizard-footer\"></ng-content> </div> </div> </div> ",
  styles: ["/** *How to define a color palette in sass */ .cf-step-header { display: flex; flex-direction: row; align-items: center; justify-content: center; } .cf-step-index-icon { color: white; line-height: 0px; display: inline-flex; width: 24px; height: 24px; align-items: center; justify-content: center; border-radius: 50%; background: #00897B; } .cf-step-index-icon, .cf-step-index-prefix { margin: 0 4px; } :host ::ng-deep .mat-tab-disabled > div > .cf-step-index-icon { background: gray; } :host ::ng-deep > .cf-wizard-container { display: flex; flex-direction: column; padding: 2px; } :host ::ng-deep > .cf-wizard-container > .cf-wizard-content > .mat-tab-group .step-ink-bar { display: none; } :host ::ng-deep > .cf-wizard-container > .cf-wizard-content > .mat-tab-group > .mat-tab-body-wrapper > .mat-tab-body { padding: 8px; } :host ::ng-deep > .cf-wizard-container.header-left > .cf-wizard-content > .mat-tab-group > .mat-tab-header, :host ::ng-deep > .cf-wizard-container.header-right > .cf-wizard-content > .mat-tab-group > .mat-tab-header { height: 100%; border-bottom: none !important; } :host ::ng-deep > .cf-wizard-container.header-left > .cf-wizard-content > .mat-tab-group > .mat-tab-header > .mat-tab-label-container, :host ::ng-deep > .cf-wizard-container.header-right > .cf-wizard-content > .mat-tab-group > .mat-tab-header > .mat-tab-label-container { height: 100%; } :host ::ng-deep > .cf-wizard-container.header-left > .cf-wizard-content > .mat-tab-group > .mat-tab-header > .mat-tab-label-container > .mat-tab-list, :host ::ng-deep > .cf-wizard-container.header-right > .cf-wizard-content > .mat-tab-group > .mat-tab-header > .mat-tab-label-container > .mat-tab-list { height: 100%; } :host ::ng-deep > .cf-wizard-container.header-left > .cf-wizard-content > .mat-tab-group > .mat-tab-header > .mat-tab-label-container > .mat-tab-list > .mat-tab-labels, :host ::ng-deep > .cf-wizard-container.header-right > .cf-wizard-content > .mat-tab-group > .mat-tab-header > .mat-tab-label-container > .mat-tab-list > .mat-tab-labels { display: flex; flex-direction: column; height: 100%; overflow-y: auto; } :host ::ng-deep > .cf-wizard-container.header-left > .cf-wizard-content > .mat-tab-group > .mat-tab-header > .mat-tab-label-container > .mat-tab-list > .mat-tab-labels > .mat-tab-label > .cf-step-header, :host ::ng-deep > .cf-wizard-container.header-right > .cf-wizard-content > .mat-tab-group > .mat-tab-header > .mat-tab-label-container > .mat-tab-list > .mat-tab-labels > .mat-tab-label > .cf-step-header { justify-content: flex-start; } :host ::ng-deep > .cf-wizard-container.header-left > .cf-wizard-content > .mat-tab-group > .mat-tab-header > .mat-tab-label-container > .mat-tab-list > .mat-tab-labels > .mat-tab-label.mat-tab-label-active > .step-ink-bar, :host ::ng-deep > .cf-wizard-container.header-right > .cf-wizard-content > .mat-tab-group > .mat-tab-header > .mat-tab-label-container > .mat-tab-list > .mat-tab-labels > .mat-tab-label.mat-tab-label-active > .step-ink-bar { top: 0; width: 2px; height: auto; } :host ::ng-deep > .cf-wizard-container.header-left > .cf-wizard-content > .mat-tab-group > .mat-tab-header > .mat-tab-label-container > .mat-tab-list > .mat-ink-bar, :host ::ng-deep > .cf-wizard-container.header-right > .cf-wizard-content > .mat-tab-group > .mat-tab-header > .mat-tab-label-container > .mat-tab-list > .mat-ink-bar { display: none !important; } :host ::ng-deep > .cf-wizard-container.header-left > .cf-wizard-content > .mat-tab-group > .mat-tab-header > .mat-tab-header-pagination, :host ::ng-deep > .cf-wizard-container.header-right > .cf-wizard-content > .mat-tab-group > .mat-tab-header > .mat-tab-header-pagination { display: none !important; } :host ::ng-deep > .cf-wizard-container.header-left > .cf-wizard-content > .mat-tab-group > .mat-tab-body-wrapper, :host ::ng-deep > .cf-wizard-container.header-right > .cf-wizard-content > .mat-tab-group > .mat-tab-body-wrapper { flex-grow: 1; } :host ::ng-deep > .cf-wizard-container.completed > .cf-wizard-content > .mat-tab-group > .mat-tab-header > div > div > .mat-ink-bar { background-color: darkgray; } :host ::ng-deep > .cf-wizard-container.completed > .cf-wizard-content > .mat-tab-group > .mat-tab-header > div > div > div > div > .step-ink-bar { background-color: darkgray !important; } :host ::ng-deep > .cf-wizard-container > .cf-wizard-footer { padding: 4px; } :host ::ng-deep > .cf-wizard-container > .cf-wizard-footer > div { display: flex; justify-content: flex-end; flex-wrap: wrap; } :host ::ng-deep > .cf-wizard-container > .cf-wizard-footer > div cf-button { margin: 4px; } :host ::ng-deep > .cf-wizard-container.header-left > .cf-wizard-content > .mat-tab-group { flex-direction: row; } :host ::ng-deep > .cf-wizard-container.header-left > .cf-wizard-content > .mat-tab-group > .mat-tab-header { border-right: 1px solid rgba(0, 0, 0, 0.12); } :host ::ng-deep > .cf-wizard-container.header-left > .cf-wizard-content > .mat-tab-group > .mat-tab-header .mat-tab-label-active > .step-ink-bar { left: 0; display: block; } :host ::ng-deep > .cf-wizard-container.header-right > .cf-wizard-content > .mat-tab-group { flex-direction: row-reverse; } :host ::ng-deep > .cf-wizard-container.header-right > .cf-wizard-content > .mat-tab-group > .mat-tab-header { border-left: 1px solid rgba(0, 0, 0, 0.12); } :host ::ng-deep > .cf-wizard-container.header-right > .cf-wizard-content > .mat-tab-group > .mat-tab-header .mat-tab-label-active > .step-ink-bar { right: 0; display: block; } :host ::ng-deep > .cf-wizard-container.footer-left { flex-direction: row-reverse; } :host ::ng-deep > .cf-wizard-container.footer-right { flex-direction: row; } :host ::ng-deep > .cf-wizard-container.footer-left > .cf-wizard-content, :host ::ng-deep > .cf-wizard-container.footer-right > .cf-wizard-content { display: flex; flex-direction: column; flex-grow: 1; width: 50%; } :host ::ng-deep > .cf-wizard-container.footer-left > .cf-wizard-footer, :host ::ng-deep > .cf-wizard-container.footer-right > .cf-wizard-footer { display: flex; } :host ::ng-deep > .cf-wizard-container.footer-left > .cf-wizard-footer > div, :host ::ng-deep > .cf-wizard-container.footer-right > .cf-wizard-footer > div { flex-direction: column; align-items: flex-end; } :host ::ng-deep > .cf-wizard-container.footer-above { flex-direction: column-reverse; } "]
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: TemplateService, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'properties': [{ type: Input },],
'styling': [{ type: Input },],
'activeStepIndex': [{ type: Input, args: ['activeStepIndex', ] },],
'showStepNumberAsIcon': [{ type: Input, args: ['showStepNumberAsIcon', ] },],
'showStepNumberAsPrefix': [{ type: Input, args: ['showStepNumberAsPrefix', ] },],
'indexBefore': [{ type: Input, args: ['indexBefore', ] },],
'headerPosition': [{ type: Input, args: ['headerPosition', ] },],
'footerPosition': [{ type: Input, args: ['footerPosition', ] },],
'backButton': [{ type: Input, args: ['backButton', ] },],
'nextButton': [{ type: Input, args: ['nextButton', ] },],
'finishButton': [{ type: Input, args: ['finishButton', ] },],
'wizardSteps': [{ type: ContentChildren, args: [CfWizardStepComponent, ] },],
'wizardFooter': [{ type: ContentChild, args: [CfWizardFooterComponent, ] },],
'wizardBack': [{ type: ContentChild, args: [CfWizardBackComponent, ] },],
'wizardNext': [{ type: ContentChild, args: [CfWizardNextComponent, ] },],
'wizardFinish': [{ type: ContentChild, args: [CfWizardFinishComponent, ] },],
'onStepChanged': [{ type: Output },],
'onComplete': [{ type: Output },],
};
}

function CfWizardComponent_tsickle_Closure_declarations() {
/** @type {?} */
CfWizardComponent.decorators;
/**
 * @nocollapse
 * @type {?}
 */
CfWizardComponent.ctorParameters;
/** @type {?} */
CfWizardComponent.propDecorators;
/**
 * It is an attribute <b>[properties]</b> of directive <b>cf-wizard</b> which take WizardModel object.
 * <p>Example of model:</p>
 * <code><pre>{{'{'}}
 *  activeStepIndex: 3,
 *  showStepNumberAsIcon: true,
 *  showStepNumberAsPrefix: false,
 *  indexBefore: true,
 *  backButton: this.myPrevBtn,
 *  nextButton: this.myNextBtn,
 *  finishButton: this.myDoneBtn,
 *  headerPosition: 'above',
 *  footerPosition: 'below'
 * {{'}'}}</pre></code>
 * <h4>activeStepIndex: number</h4>
 * Active step index
 * <h4>showStepNumberAsIcon: boolean</h4>
 * Showing step number as icon in wizard header steps buttons
 * <h4>showStepNumberAsPrefix: boolean</h4>
 * Showing step prefix as icon in wizard header steps buttons
 * <h4>indexBefore: boolean</h4>
 * Showing header step index (prefix or icon) before step label
 * <h4>backButton: ButtonModel</h4>
 * ButtonModel object for wizard previous button
 * <h4>nextButton: ButtonModel</h4>
 * ButtonModel object for wizard next button
 * <h4>finishButton: ButtonModel</h4>
 * ButtonModel object for wizard finish button
 * <h4>headerPosition: string</h4>
 * Header position: above (default), right, below, left
 * <h4>footerPosition: string</h4>
 * Footer position: above, right, below (default), left
 * @type {?}
 */
CfWizardComponent.prototype.properties;
/**
 * It is an attribute <b>[styling]</b> of directive <b>cf-wizard</b> which take WizardStylingModel object with next properties:
 * <h4>headerSection: StylingModel</h4>
 * Styling properties for wizard header container
 * <h4>container: StylingModel</h4>
 * Styling properties for main wizard container
 * <h4>navigationSection: StylingModel</h4>
 * Styling properties for wizard bottom navigation section
 * <h4>backButton: ButtonStylingModel</h4>
 * Styling properties for wizard previous button
 * <h4>nextButton: ButtonStylingModel</h4>
 * Styling properties for wizard next button
 * <h4>finishButton: ButtonStylingModel</h4>
 * Styling properties for wizard finish button
 * @type {?}
 */
CfWizardComponent.prototype.styling;
/**
 * @hidden 
 * It is an array of wizard steps
 * @type {?}
 */
CfWizardComponent.prototype.wizardSteps;
/**
 * @hidden
 * @type {?}
 */
CfWizardComponent.prototype.wizardFooter;
/**
 * @hidden
 * @type {?}
 */
CfWizardComponent.prototype.wizardBack;
/**
 * @hidden
 * @type {?}
 */
CfWizardComponent.prototype.wizardNext;
/**
 * @hidden
 * @type {?}
 */
CfWizardComponent.prototype.wizardFinish;
/**
 * @hidden
 * @type {?}
 */
CfWizardComponent.prototype._activeStep;
/**
 * @hidden 
 * Property which means if wizard Finish step was clicked
 * @type {?}
 */
CfWizardComponent.prototype._isCompleted;
/**
 * Output event with active step index
 * @type {?}
 */
CfWizardComponent.prototype.onStepChanged;
/**
 * Output event on wizard work finish
 * @type {?}
 */
CfWizardComponent.prototype.onComplete;
/** @type {?} */
CfWizardComponent.prototype.elementRef;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
